import {
	clone,
	filter,
	flatten,
	groupBy,
	isArray,
	map,
	pipe,
	prop,
	uniqBy,
} from 'lodash/fp'

export type TreeNodeType = {
	id: string
	name: string
	data?: any
	level: number
	visible: boolean
	parent?: TreeNodeType
	children: TreeNodeType[]
}

export const toggleItem = (list: any[], id: string | number) =>
	list.includes(id) ? list.filter((i) => i != id) : [...list, id]

/* export const nest = (items, id = null, link = 'parent') =>
	items
		.filter((item) => item[link] === id)
		.map((item) => ({ element: item, children: nest(items, item.id) }))
 */

/**
 * Create tree from dataset
 * @param items - array of items
 * @example
 * ```ts
 * const tree = getTree([
 *   { id: '1', name: 'Node 1', parent: null, level: 1 },
 *   { id: '2', name: 'Node 2', parent: '1', level: 2 },
 *   { id: '3', name: 'Node 3', parent: '1', level: 2 },
 * ])
 * console.log(tree)
 * // Output:
 * // [
 * //   { id: '1', name: 'Node 1', parent: null, level: 1, children: [{ id: '2', name: 'Node 2', parent: '1', level: 2, children: [] }, { id: '3', name: 'Node 3', parent: '1', level: 2, children: [] }] }
 * // ]
 * ```
 * @returns TreeNode[]
 */
export function getTree(
	items: {
		parent?: string | null
		id: string
		name: string
		level?: number
		[key: string]: any
	}[]
): TreeNodeType[] {
	const byParent = groupBy<any>('parent', items)

	if (!items.length && !!byParent) return []

	const getItems = (nodes: any[], lvl: number, parent?: TreeNodeType) => {
		const sortedNodes = nodes?.sort((a, b) =>
			(a?.name || '').localeCompare(b?.name || '', undefined, {
				numeric: true,
				sensitivity: 'base',
			})
		)

		return sortedNodes.map(({ id, name, level }: any) => {
			const node: TreeNodeType = {
				parent,
				id,
				name,
				visible: true,
				level: level !== undefined ? level : lvl,
				children: [],
			}
			node.children = byParent[id]
				? getItems(byParent[id], lvl + 1, { ...node })
				: []

			return node as TreeNodeType
		})
	}

	return getItems(byParent['null'], 1)
}

/**
 * Function to get children ids from tree with nested nodes
 * @param nodes - tree nodes
 * @param ids - ids of existed
 * @returns - string[]
 */
export function getChildrenIds(
	nodes: TreeNodeType[],
	ids: string[] = []
): string[] {
	return nodes.reduce((acc, { id, children }) => {
		if (children?.length) {
			return getChildrenIds(children, acc)
		}

		return [...acc, id]
	}, ids)
}

/**
 * Finds a node in a tree structure based on a given condition.
 *
 * @param data - The tree data to search in.
 * @param check - The condition to check for each node.
 * @returns The first node that satisfies the condition, or undefined if no node is found.
 */
export function findNodeOfTree(
	data: TreeNodeType[],
	check: (item: TreeNodeType) => boolean
) {
	const findNode = (
		nodes: readonly TreeNodeType[] = []
	): TreeNodeType | undefined => {
		for (let i = 0; i < nodes.length; i += 1) {
			const item = nodes[i]
			if (isArray(item.children)) {
				const node = findNode(item.children)
				if (node) {
					return node
				}
			}

			if (check(item)) {
				return item
			}
		}

		return undefined
	}

	return findNode(data)
}

type HasChildren<T extends Record<string, unknown>> = T & {
	children?: readonly HasChildren<T>[]
}
/**
 * Filters the nodes of a tree based on a given condition.
 *
 * @template TItem - The type of the tree nodes.
 * @param data - The array of tree nodes.
 * @param check - The condition function to filter the nodes.
 * @returns An array of filtered tree nodes.
 */
export function filterNodesOfTree<
	TItem extends HasChildren<Record<string, unknown>>,
>(data: readonly TItem[], check: (item: TItem) => boolean): TItem[] {
	const findNodes = (nodes: readonly TItem[] = []) => {
		const nextNodes: TItem[] = []
		for (let i = 0; i < nodes.length; i += 1) {
			if (isArray(nodes[i].children)) {
				const nextChildren = findNodes(nodes[i].children as TItem[])
				if (nextChildren.length) {
					const item = clone(nodes[i])
					item.children = nextChildren
					nextNodes.push(item)
					continue
				}
			}

			if (check(nodes[i])) {
				nextNodes.push(nodes[i])
			}
		}

		return nextNodes
	}

	return findNodes(data)
}
/**
 * Function to find nodes by ids in tree with nested nodes
 * @param nodes - tree nodes
 * @param ids - ids of nodes to find
 * @returns  TreeNode[]
 */
export function findNodes(
	nodes: TreeNodeType[],
	ids: string[]
): TreeNodeType[] {
	return nodes.reduce((acc, node) => {
		if (ids.includes(node.id)) {
			return [...acc, node]
		} else if (node.children?.length) {
			return [...acc, ...findNodes(node.children, ids)]
		}

		return acc
	}, [] as TreeNodeType[])
}
/**
 * Function to find nodes by ids in tree with nested nodes
 * @param nodes - tree nodes
 * @param ids - ids of nodes to find
 * @returns  TreeNode[]
 */
export function findAllNodes(
	nodes: TreeNodeType[],
	ids: string[],
	accumulator: TreeNodeType[] = []
): TreeNodeType[] {
	const acc = accumulator
	const find = (nodes: TreeNodeType[], ids: string[]) => {
		for (let index = 0; index < nodes.length; index++) {
			const node = nodes[index]
			if (ids.indexOf(node.id) !== -1) {
				acc.push(node)
				if (node.children?.length) {
					find(node.children, ids)
				}
			}
		}
	}
	find(nodes, ids)

	return acc
}

export function normalizeToEndNodes(
	tree: TreeNodeType[],
	ids: string[]
): string[] {
	return getChildrenIds(findNodes(tree, ids))
}

/**
 * get all ancestor nodes of given node
 * @param {*} node
 */
export function getNodeParents(
	node: TreeNodeType,
	parentKey = 'parent',
	valueKey?: string
) {
	const parents: any[] = []
	const traverse = (node: any) => {
		if (node?.[parentKey]) {
			traverse(node[parentKey])
			if (valueKey) {
				parents.push(node[parentKey][valueKey])
			} else {
				parents.push(node[parentKey])
			}
		}
	}

	traverse(node)

	return parents
}

export const getIsAllSelected = (
	nodes: TreeNodeType[],
	selected: string[]
): boolean =>
	nodes.every(({ id, children }) =>
		children?.length
			? getIsAllSelected(children, selected)
			: selected.includes(id)
	)

export const getAtLeastOneSelected = (
	nodes: TreeNodeType[],
	selected: string[]
): boolean =>
	nodes.some(({ id, children }) =>
		children?.length
			? getAtLeastOneSelected(children, selected)
			: selected.includes(id)
	)

export const getStatisticsByLevels: (
	nodes: TreeNodeType[]
) => Record<number, TreeNodeType[]> = pipe([
	map((node: TreeNodeType) => [node, ...getNodeParents(node)]),
	flatten,
	uniqBy('id'),
	groupBy(prop('level')),
])

/**
 * Compacts selected leaf node IDs to the nearest fully-selected ancestor nodes.
 * Returns the working level and the list of compacted selected nodes.
 *
 * @param tree - The tree structure to search in
 * @param selectedIds - Array of selected leaf node IDs
 * @returns Object with `level` (the working level) and `selected` (compacted TreeNodeType[])
 *
 * @example
 * // If all children of a parent are selected, returns the parent instead
 * // Selected: ['2.1.1','2.1.2','2.1.3','3.1.1','3.1.2','3.1.3']
 * // Returns: { level: 2, selected: [node_2.1, node_3.1] }
 *
 * @example
 * // If not all children are selected, returns individual nodes at the lowest level
 * // Selected: ['1.1.3','3.1.1','3.2.1','3.2.2','3.2.3']
 * // Returns: { level: 3, selected: [node_1.1.3, node_3.1.1, node_3.2.1, node_3.2.2, node_3.2.3] }
 */
export function getCompactedSelection(
	tree: TreeNodeType[],
	selectedIds: string[]
): { level: number; selected: TreeNodeType[] } {
	if (!selectedIds.length || !tree.length) {
		return { level: 1, selected: [] }
	}

	const selectedSet = new Set(selectedIds)

	// Check if all leaf descendants of a node are selected
	const isFullySelected = (node: TreeNodeType): boolean => {
		if (!node.children?.length) {
			return selectedSet.has(node.id)
		}

		return node.children.every((child) => isFullySelected(child))
	}

	// Check if any leaf descendant of a node is selected
	const hasSelectedDescendant = (node: TreeNodeType): boolean => {
		if (!node.children?.length) {
			return selectedSet.has(node.id)
		}

		return node.children.some((child) => hasSelectedDescendant(child))
	}

	// Check if selection can be perfectly represented at a given level
	const canRepresentAtLevel = (
		nodes: TreeNodeType[],
		targetLevel: number
	): boolean => {
		for (const node of nodes) {
			if (node.level === targetLevel) {
				// At target level: if it has selected descendants, ALL must be selected
				if (hasSelectedDescendant(node) && !isFullySelected(node)) {
					return false
				}
			} else if (node.level < targetLevel && !node.children?.length) {
				// Leaf node above target level - always representable
				continue
			} else if (node.children?.length) {
				// Not at target level yet, recurse
				if (!canRepresentAtLevel(node.children, targetLevel)) {
					return false
				}
			}
		}

		return true
	}

	// Get selected nodes at a given level
	const getSelectedAtLevel = (
		nodes: TreeNodeType[],
		targetLevel: number
	): TreeNodeType[] => {
		const result: TreeNodeType[] = []
		for (const node of nodes) {
			if (node.level === targetLevel) {
				if (isFullySelected(node)) {
					result.push(node)
				}
			} else if (node.level < targetLevel && !node.children?.length) {
				// Leaf node above target level - include if selected
				if (selectedSet.has(node.id)) {
					result.push(node)
				}
			} else if (node.children?.length) {
				result.push(...getSelectedAtLevel(node.children, targetLevel))
			}
		}

		return result
	}

	// Find the maximum level in the tree
	const findMaxLevel = (nodes: TreeNodeType[]): number => {
		let maxLvl = 0
		for (const node of nodes) {
			maxLvl = Math.max(maxLvl, node.level)
			if (node.children?.length) {
				maxLvl = Math.max(maxLvl, findMaxLevel(node.children))
			}
		}

		return maxLvl
	}

	const maxLevel = findMaxLevel(tree)

	// Find the minimum (highest in tree) level that can represent the selection
	let targetLevel = maxLevel
	for (let level = 1; level <= maxLevel; level++) {
		if (canRepresentAtLevel(tree, level)) {
			targetLevel = level
			break
		}
	}

	// Get selected nodes at the target level
	const selected = getSelectedAtLevel(tree, targetLevel)

	return { level: targetLevel, selected }
}
